import React, { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { hasLuxPlusSubscriptionStatus } from '~/components/Membership/utils';

import { FRAUD_STATUS } from '~/consts/order';
import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER } from '~/consts/roles';

import DiscoveryService from '~/services/DiscoveryService';
import { getCustomerMembershipSubscriptions } from '~/services/MembershipService';
import { getPurchases } from '~/services/OrdersService';
import { getCredits } from '~/services/PaymentsService';
import UsersService from '~/services/UsersService';

import UserBreadcrumbs from '../../Common/Elements/UserBreadcrumbs';
import PermissionedComponent from '../../Common/PermissionedComponent';
import Spinner from '../../Common/Spinner';
import { Credits } from '../Credits/types';
import ForceLogoutModal from '../ForceLogoutModal';
import ResolveAccountTakeoverInstructions from '../UserDetails/ResolveAccountTakeoverInstructions';
import TenancyWarningMessage from '../UserDetails/TenancyWarningMessage';

import FraudWarning from './FraudWarning';
import { MarkAllFraud } from './MarkAllFraud';
import { MarkForwardFraud } from './MarkForwardFraud';
import { MarkOrderFraud } from './MarkOrderFraud';
import SamePhoneAccounts from './SamePhoneAccounts';
import { SingleCreditTransaction } from './SingleCreditTransaction';
import { SingleOrder } from './SingleOrder';
import { countriesMap } from './countriesMap';
import * as utils from './utils';

type Props = RouteComponentProps<{ id_user: string }>;

const ORDERS_PER_PAGE = 50;

export default function UserHistory({ match }: Props) {
  const { id_user: userId } = match.params;
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState<string | null>(null);
  const [user, setUser] = useState<App.User | null>(null);
  const [ipCache, setIpCache] = useState<Record<string, any> | null>(null);
  const [subscriptions, setSubscriptions] = useState<App.MembershipSubscriptions | null>(null);
  const [isForceLogoutModalVisible, setIsForceLogoutModalVisible] = useState(false);
  const [purchases, setPurchases] = useState<Array<App.Order> | null>(null);
  const [userChanges, setUserChanges] = useState<any>(null);
  const [logins, setLogins] = useState<any>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const tenant = useSelector((state: App.State) => state.tenant);
  const [showLogins, setShowLogins] = useState(true);
  const [showCompletedOrders, setShowCompletedOrders] = useState(true);
  const [showPaymentFailedOrders, setShowPaymentFailedOrders] = useState(true);
  const [showAbandonedOrders, setShowAbandonedOrders] = useState(true);
  const [showUserChanges, setShowUserChanges] = useState(true);
  const [showCreditTransactions, setShowCreditTransactions] = useState(true);
  const [newUserId, setNewUserId] = useState('');

  // Deprecated: switching is now handled via paste only

  // No keypress handling needed; switching is triggered on paste of a full UUID

  const isValidUserId = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');
    const trimmed = pastedText.trim();

    setNewUserId(trimmed);
    if (isValidUserId(trimmed)) {
      event.preventDefault();
      history.push(`/users/${trimmed}/history`);
      setNewUserId('');
    }
  };

  const shadowBanMutation = useMutation({
    mutationFn: async (newShadowBanValue: boolean) => {
      return await UsersService.toggle({
        id_member: userId,
        toggle_name: 'shadow_ban_user',
        toggle_value: newShadowBanValue,
      });
    },
    onSuccess: (_data, newShadowBanValue) => {
      setUser((prevUser) => (prevUser ? { ...prevUser, shadow_ban_user: newShadowBanValue } : null));
      enqueueSnackbar(`User ${newShadowBanValue ? 'shadow banned' : 'un-shadow banned'} successfully`, {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update shadow ban status: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  const handleToggleShadowBan = () => {
    if (!user) return;
    shadowBanMutation.mutate(!user.shadow_ban_user);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const userData = await UsersService.getUser(userId, { brand: tenant.brand });
        const [subscriptionsResponse, firstPagePurchasesResponse, loginsResponse, userHistoryResponse] =
          await Promise.all([
            getCustomerMembershipSubscriptions({ customerId: userData.id_member }).catch(() => {
              // Returns 404 for non-members, which triggers an error - but we don't want that.
              return { result: null };
            }),
            getPurchases({
              page: 1,
              per_page: ORDERS_PER_PAGE,
              brand: tenant.brand,
              customer_id: userData.id_member,
            }),
            UsersService.getUserLogins(userId, 100),
            UsersService.getUserHistory(userId),
          ]);

        // Fetch all remaining pages of orders if there are more
        let allOrders = [...firstPagePurchasesResponse.result];
        const totalOrders = firstPagePurchasesResponse.total || 0;
        const totalPages = Math.ceil(totalOrders / ORDERS_PER_PAGE);

        if (totalPages > 1) {
          // Fetch remaining pages in parallel
          const remainingPagePromises = [];
          for (let page = 2; page <= totalPages; page++) {
            remainingPagePromises.push(
              getPurchases({
                page,
                per_page: ORDERS_PER_PAGE,
                brand: tenant.brand,
                customer_id: userData.id_member,
              }),
            );
          }
          const remainingPagesResponses = await Promise.all(remainingPagePromises);
          // Combine all orders from all pages
          allOrders = [...allOrders, ...remainingPagesResponses.flatMap((response) => response.result)];
        }

        // Create a combined purchases response
        const purchasesResponse = {
          ...firstPagePurchasesResponse,
          result: allOrders,
        };

        // Extract unique currencies from orders
        const currencyCodes: Array<string> = purchasesResponse.result
          .map((order) => order.currency_code)
          .filter((code): code is string => typeof code === 'string' && code.length > 0);
        const uniqueCurrencies: Array<string> = Array.from(new Set(currencyCodes));
        // If no orders, default to AUD
        const currenciesToFetch: Array<string> = uniqueCurrencies.length > 0 ? uniqueCurrencies : ['AUD'];

        // Fetch credits for each currency in parallel
        const creditsPromises = currenciesToFetch.map((currency: string) =>
          getCredits(userId, currency, tenant.brand, null).catch(() => {
            // Returns error if no credits exist for this currency - return empty result
            return { result: { collection: [], available_credit: '0', led_next_expiring: null } };
          }),
        );

        const creditsResponses = await Promise.all(creditsPromises);

        // Combine all credit transactions from all currencies into one array
        const allCreditTransactions = creditsResponses.flatMap((response) => response.result.collection || []);

        // Create a combined credits response object
        const combinedCreditsResponse: Credits = {
          status: 200,
          message: 'OK',
          result: {
            collection: allCreditTransactions,
            available_credit: '0', // Not meaningful when combining currencies
            led_next_expiring: null, // Not meaningful when combining currencies
          },
          metadata: {
            available_credit: '0',
            total_available: 0,
            total_returned: 0,
          },
        };

        const loginIps = loginsResponse.map((login) => login.ip_address.replace(/\/32$/, ''));
        const orderIps = purchasesResponse.result.map((order) => order.device_ip);
        const allIps = [...loginIps, ...orderIps];
        const ipLocations = await DiscoveryService.getIpLocations(allIps);
        const ipCache = ipLocations.reduce((acc, ip) => {
          acc[ip.query] = ip;
          return acc;
        }, {});

        setIpCache(ipCache);
        setUser(userData);
        setSubscriptions(subscriptionsResponse);
        setPurchases(purchasesResponse.result);
        setLogins(loginsResponse);
        setUserChanges(userHistoryResponse.history);
        setCredits(combinedCreditsResponse);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, tenant.brand]);

  const handleOrderUpdated = (updatedOrder: App.Order) => {
    if (!purchases) {
      return;
    }

    const updatedPurchases = purchases.map((purchase) => (purchase.id === updatedOrder.id ? updatedOrder : purchase));
    setPurchases(updatedPurchases);
  };

  const handleOrdersUpdated = (updatedOrders: Array<App.Order>) => {
    setPurchases(updatedOrders);
  };

  const showForceLogoutModal = () => {
    setIsForceLogoutModalVisible(true);
  };

  const hideForceLogoutModal = () => {
    setIsForceLogoutModalVisible(false);
  };

  const getOrderItemStatuses = (order: App.Order): Array<string> => {
    const itemStatuses: Array<string> = [];
    // Get all item arrays (accommodation_items, tour_items, etc.)
    const itemKeys = Object.keys(order).filter((k) => k.endsWith('_items'));
    itemKeys.forEach((key) => {
      const items = order[key];
      if (Array.isArray(items)) {
        items.forEach((item) => {
          if (item.status && !itemStatuses.includes(item.status)) {
            itemStatuses.push(item.status);
          }
        });
      }
    });
    return itemStatuses;
  };

  if (isLoading || !user || !subscriptions || !purchases || !logins || !userChanges || !credits) {
    return <Spinner />;
  }

  const combinedHistory = utils.combineHistory(logins, purchases, userChanges, credits?.result.collection || []);
  const filteredHistory = combinedHistory.filter((item) => {
    if (item.type === 'login') {
      return showLogins;
    }
    if (item.type === 'order') {
      if (item.object.status === 'completed') {
        return showCompletedOrders;
      }
      if (item.object.status === 'payment_failed') {
        return showPaymentFailedOrders;
      }
      if (item.object.status === 'abandoned') {
        return showAbandonedOrders;
      }
    }
    if (item.type === 'user_change' || item.type === 'phone_change') {
      return showUserChanges;
    }
    if (item.type === 'credit_transaction') {
      return showCreditTransactions;
    }
    return true;
  });

  const historyColumns: Array<GridColDef> = [
    {
      field: 'created_at',
      headerName: 'Date',
      flex: 1,
      renderCell: (params) => (
        <code>
          <strong>
            {new Date(params.value).toLocaleString('en-AU', {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
            })}
          </strong>
          <br />
          {new Date(params.value).toLocaleString('en-AU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })}
        </code>
      ),
      display: 'flex',
    },
    {
      field: 'ip_location',
      headerName: 'IP Location',
      flex: 2,
      renderCell: (params) => {
        if (params.row.type === 'credit_transaction') {
          return '-';
        }
        let ip;
        if (params.row.type === 'order') {
          ip = params.row.object.device_ip;
        } else if (params.row.type === 'login') {
          ip = params.row.object.ip_address.replace(/\/32$/, '');
        } else {
          return '-';
        }
        const ipInfo = ipCache[ip];
        if (!ipInfo) {
          return ip;
        }
        const { city, country, region, countryCode, query, proxy, hosting, mobile } = ipInfo;
        return (
          <div>
            <Tooltip title={`${city}, ${region}, ${country}`} placement="left">
              <>
                <span>
                  {countriesMap[countryCode]?.flag} {city}
                </span>{' '}
                <a href={`https://ip-api.com/#${query}`} target="_blank" rel="noreferrer">
                  ?
                </a>
              </>
            </Tooltip>{' '}
            <Box ml={3}>
              {proxy && (
                <Tooltip
                  title="The user was using a proxy or VPN. Fraudsters may use these to hide their location."
                  placement="left"
                >
                  <div> 🌎 proxy</div>
                </Tooltip>
              )}
              {/* Most proxys will be a hosting provider, so hosting is redundant info if proxy is true */}
              {hosting && !proxy && (
                <Tooltip
                  title="This was likely from a server, not a personal computer. Fraudsters use these to bulk-run scripts."
                  placement="left"
                >
                  <div> ⚠️ hosting</div>
                </Tooltip>
              )}
              {mobile && (
                <Tooltip title="The internet connection was from a mobile device." placement="left">
                  <div> 📱 mobile</div>
                </Tooltip>
              )}
            </Box>
          </div>
        );
      },
    },
    {
      field: 'relative_date',
      headerName: 'Days',
      flex: 0.5,
      renderCell: (params) => {
        if (!currentDate) {
          return '';
        }

        const rowDate = new Date(params.row.created_at);
        const selectedDate = new Date(currentDate);

        // Get difference in days
        const diffDays = Math.round((rowDate.getTime() - selectedDate.getTime()) / (1000 * 60 * 60 * 24));

        return (
          <code>
            <strong>{diffDays}d</strong>
          </code>
        );
      },
      display: 'flex',
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        const className = params.row.object.status === 'completed' ? 'success' : 'error';
        if (params.row.type === 'order') {
          const itemStatuses = getOrderItemStatuses(params.row.object);
          return (
            <Box>
              <Chip label={params.row.object.status} color={className} variant="outlined" size="small" />
              {itemStatuses.length > 0 && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  items:{' '}
                  {itemStatuses.map((status, index) => (
                    <span key={status}>
                      {index > 0 && ', '}
                      <Typography
                        component="span"
                        sx={{
                          color: status === 'cancelled' ? 'error.main' : 'text.primary',
                        }}
                      >
                        {status}
                      </Typography>
                    </span>
                  ))}
                </Typography>
              )}
              {[FRAUD_STATUS.MANUAL_REVIEWED_FRAUD, FRAUD_STATUS.AUTO_REVIEWED_FRAUD].includes(
                params.row.object.fraud_status,
              ) && (
                <div>
                  <Alert severity="error">Fraud</Alert>
                </div>
              )}

              <MarkOrderFraud order={params.row.object} onOrderUpdated={handleOrderUpdated} />
              <MarkForwardFraud order={params.row.object} allOrders={purchases} onOrdersUpdated={handleOrdersUpdated} />
            </Box>
          );
        }
        if (params.row.type === 'user_change' || params.row.type === 'phone_change') {
          return (
            <div>
              <Chip label={params.row.type.replace('_', ' ')} color="info" variant="outlined" size="small" /> <br />
              {params.row.object.updated_by === user.id_member ? (
                <em>(self-service)</em>
              ) : params.row.object.updated_by ? (
                <em>
                  by{' '}
                  {params.row.object.updated_by?.includes('@') ? (
                    params.row.object.updated_by
                  ) : (
                    <Link href={`/users/${params.row.object.updated_by}`}>LE Agent</Link>
                  )}
                </em>
              ) : null}
            </div>
          );
        }
        if (params.row.type === 'credit_transaction') {
          return <Chip label="Credit Transaction" color="info" variant="outlined" size="small" />;
        }
        return <Chip label={params.row.type} color="info" variant="outlined" size="small" />;
      },
    },
    {
      field: 'object',
      headerName: 'Object',
      renderCell: (params) => {
        const { type, object } = params.row;
        if (type === 'order') {
          return <SingleOrder order={object} />;
        } else if (type === 'login') {
          return (
            <>
              via {object.login_method}
              {'\u00A0'}
              {object.login_method == 'sms-otp' && (
                <Tooltip title="Unless the fraudster recently changed the account phone number, this login is the real user. They logged in with a one-time password that was sent to their own phone number.">
                  <span>🟢</span>
                </Tooltip>
              )}
            </>
          );
        } else if (type === 'user_change' || type === 'phone_change') {
          const presentValue = (value: any) => {
            if (typeof value === 'boolean') {
              const color = value ? 'success.main' : 'error.main';
              return (
                <Box component="span" sx={{ color }}>
                  {value.toString()}
                </Box>
              );
            }
            return value || '(null)';
          };
          return (
            <Box>
              {object.changed_fields.map((field) => {
                if (field === 'password') {
                  return <strong>Password changed</strong>;
                }
                return (
                  <div key={field}>
                    <strong>{field}</strong> from <em>{presentValue(object.previous_values[field])}</em> to{' '}
                    <em>{presentValue(object.new_values[field])}</em>
                  </div>
                );
              })}
              <Tooltip title="After accessing an account, fraudsters often change account details to lock the real customer out. Was there also a recent login from a new location? Or a short-lead-time booking?">
                <span>🟠</span>
              </Tooltip>
            </Box>
          );
        } else if (type === 'credit_transaction') {
          return <SingleCreditTransaction credit={object} />;
        }
        return <div>unhandled row type</div>;
      },
      flex: 4,
      display: 'flex',
    },
  ];
  return (
    <Container maxWidth="xl">
      <UserBreadcrumbs
        userId={userId}
        user={{ label: user.fullName || user.email || 'User', href: `/users/${userId}` }}
        currentPage="History"
      />
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {user.fullName}
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          <Link href={`/users/${user.id_member}`}>Back to user</Link> • {user.email} •{' '}
          {hasLuxPlusSubscriptionStatus(subscriptions) ? 'LuxPlus Member' : 'Standard Member'} •{' '}
          {user.shadow_ban_user ? 'Shadowbanned' : 'Not banned'} •{' '}
          <Link
            href={`https://platform.openai.com/logs?api=responses&metadata%5Buser_id%5D=${userId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open AI logs
          </Link>
        </Typography>
        <Stack direction="row" alignItems="start" flexWrap="wrap" gap={2} sx={{ mt: 2 }}>
          <MarkAllFraud purchases={purchases} userId={userId} />
          <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
            <Button variant="contained" color="error" onClick={showForceLogoutModal}>
              Force logout
            </Button>
          </PermissionedComponent>
          <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER]}>
            <Button
              variant="contained"
              color={user.shadow_ban_user ? 'warning' : 'error'}
              onClick={handleToggleShadowBan}
              disabled={shadowBanMutation.isPending}
              startIcon={shadowBanMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {user.shadow_ban_user ? 'Un-Shadow ban user' : 'Shadow ban user'}
            </Button>
          </PermissionedComponent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              label="Switch to User ID"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              onPaste={handlePaste}
              size="small"
              placeholder="Enter user ID"
              sx={{ minWidth: 200 }}
            />
          </Stack>
        </Stack>
      </Box>
      <FraudWarning userId={userId} />
      <TenancyWarningMessage user={user} />
      <ResolveAccountTakeoverInstructions user={user} alwaysShow />
      <SamePhoneAccounts user={user} />
      <Box sx={{ mb: 2 }}>
        <FormGroup row>
          <FormControlLabel
            control={<Checkbox checked={showLogins} onChange={(e) => setShowLogins(e.target.checked)} />}
            label="Logins"
          />
          <FormControlLabel
            control={
              <Checkbox checked={showCompletedOrders} onChange={(e) => setShowCompletedOrders(e.target.checked)} />
            }
            label="completed orders"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showPaymentFailedOrders}
                onChange={(e) => setShowPaymentFailedOrders(e.target.checked)}
              />
            }
            label="payment_failed orders"
          />
          <FormControlLabel
            control={
              <Checkbox checked={showAbandonedOrders} onChange={(e) => setShowAbandonedOrders(e.target.checked)} />
            }
            label="abandoned orders"
          />
          <FormControlLabel
            control={<Checkbox checked={showUserChanges} onChange={(e) => setShowUserChanges(e.target.checked)} />}
            label="user changes"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showCreditTransactions}
                onChange={(e) => setShowCreditTransactions(e.target.checked)}
              />
            }
            label="credit transactions"
          />
        </FormGroup>
      </Box>
      <DataGrid
        autoHeight
        rows={filteredHistory || []}
        columns={historyColumns}
        getRowId={(row) => JSON.stringify(row)} // hacky but created_at isn't unique - some rows have the exact same value which confuses the grid, it thinks they're the same row & shows duplicates
        getRowHeight={() => 'auto'}
        loading={isLoading}
        disableColumnMenu
        disableRowSelectionOnClick
        density="compact"
        onRowClick={(params) => {
          setCurrentDate(params.row.created_at);
        }}
        getCellClassName={(params) => {
          if (params.row.status && params.row.status !== 'completed') {
            return 'MuiDataGrid-cell--incomplete';
          }
          if (!currentDate || params.field !== 'relative_date') {
            return '';
          }

          const rowDate = new Date(params.row.created_at);
          const selectedDate = new Date(currentDate);

          // Check if same day
          if (rowDate.toDateString() === selectedDate.toDateString()) {
            return 'MuiDataGrid-cell--same-day';
          }

          // Get difference in days
          const diffDays = Math.abs(Math.floor((rowDate.getTime() - selectedDate.getTime()) / (1000 * 60 * 60 * 24)));

          if (diffDays <= 7) {
            return 'MuiDataGrid-cell--within-a-week';
          }
          if (diffDays <= 14) {
            return 'MuiDataGrid-cell--within-two-weeks';
          }

          return '';
        }}
        sx={{
          '& .MuiDataGrid-cell--same-day': {
            backgroundColor: '#c0e3ff !important',
          },
          '& .MuiDataGrid-cell--within-a-week': {
            backgroundColor: '#d4e8fb !important',
          },
          '& .MuiDataGrid-cell--within-two-weeks': {
            backgroundColor: '#e9f3fb !important',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            '& .MuiDataGrid-cell': {
              backgroundColor: '#fafafa',
            },
          },
        }}
      />
      <ForceLogoutModal isOpen={isForceLogoutModalVisible} user={user} onClose={hideForceLogoutModal} />
    </Container>
  );
}
